/**
 * Con qué entiende el asistente de WhatsApp lo que le escriben.
 *
 * `none` es entender por palabras clave: funciona sin mandarle nada a nadie y es
 * como nace. Con un proveedor elegido, lo que la gente escribe se le envía a esa
 * empresa para que diga qué se pidió — sólo eso: las cifras las pone siempre el
 * portal. Por eso elegirlo es del superusuario y de nadie más.
 */
export type AssistantProvider = 'none' | 'openai' | 'anthropic';

export type ModelProvider = Exclude<AssistantProvider, 'none'>;

export const MODEL_PROVIDERS: readonly ModelProvider[] = ['openai', 'anthropic'];

export const PROVIDER_LABEL: Readonly<Record<ModelProvider, string>> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
};

/**
 * Lo que el portal sabe de una llave guardada: en qué termina y quién la puso.
 *
 * **El valor no está aquí porque el servidor no lo devuelve**, ni a quien lo
 * escribió. Una llave entra por la pantalla y no vuelve a salir.
 */
export interface AssistantKeyStatus {
  readonly tail: string;
  readonly updatedAt: Date | null;
  readonly updatedByName: string;
  /** `false`: está guardada pero el servidor ya no puede descifrarla. Hay que volver a pegarla. */
  readonly readable: boolean;
}

export interface AssistantSettings {
  readonly provider: AssistantProvider;
  readonly keys: Readonly<Record<ModelProvider, AssistantKeyStatus | null>>;
  /** El modelo que usa cada proveedor. Lo fija el servidor; aquí sólo se enseña. */
  readonly models: Readonly<Record<ModelProvider, string>>;
  /** `false` mientras al secreto del asistente le falte la pieza con que se cifran las llaves. */
  readonly canStoreKeys: boolean;
  /** La otra mitad: si el asistente tiene con qué hablar con WhatsApp. */
  readonly whatsapp: {
    readonly connected: boolean;
    readonly numberTail: string | null;
    readonly dryRun: boolean;
  };
}

/** El resultado de preguntarle algo de verdad al modelo, con la llave guardada. */
export interface AssistantModelTest {
  readonly ok: boolean;
  readonly detail: string;
}

/** Si un proveedor se puede elegir: tiene llave y el servidor la puede leer. */
export const providerReady = (settings: AssistantSettings, provider: ModelProvider): boolean =>
  settings.keys[provider]?.readable === true;
